import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Sector } from 'recharts';
import { Loader2, Brain } from 'lucide-react';

interface SensorDistributionChartProps {
  data: Array<{ type: string; count: number }>;
  loading?: boolean;
}

// Theme-compliant color palette using CSS variables and theme colors
const COLORS = [
  'hsl(var(--chart-1))', // Primary teal
  'hsl(var(--chart-2))', // Blue
  'hsl(var(--chart-3))', // Indigo
  'hsl(var(--chart-6))', // Red/Orange
  'hsl(var(--chart-7))', // Amber
  'hsl(var(--chart-8))', // Green
  'hsl(var(--primary))', // Primary brand color
  'hsl(var(--success))', // Success green
  'hsl(var(--warning))', // Warning amber
  'hsl(var(--info))', // Info blue
  'hsl(var(--destructive))', // Error red
  'hsl(var(--accent))', // Accent color
  'hsl(var(--secondary))', // Secondary color
  'hsl(var(--muted))', // Muted color
  'hsl(var(--border))', // Border color
];

// Function to capitalize first letter of each word
const capitalize = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Custom active shape for better hover effect with responsive sizing
const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;

  return (
    <g>
      <text
        x={cx}
        y={cy - 15}
        dy={8}
        textAnchor="middle"
        fill="hsl(var(--muted-foreground))"
        fontSize={12}
        className="font-medium"
      >
        {capitalize(payload.name)}
      </text>
      <text x={cx} y={cy + 5} dy={8} textAnchor="middle" fill={fill} fontWeight="bold" fontSize={14}>
        {value}
      </text>
      <text x={cx} y={cy + 25} dy={8} textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize={11}>
        {`${(percent * 100).toFixed(1)}%`}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.9}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={innerRadius - 4}
        outerRadius={innerRadius - 1}
        fill={fill}
      />
    </g>
  );
};

// Custom legend that shows percentage with responsive design
const renderLegend = (props: any) => {
  const { payload } = props;
  const total = payload.reduce((sum: number, entry: any) => sum + entry.payload.value, 0);

  // Limit displayed items for better UX on small screens
  const displayItems = payload.slice(0, 6); // Show max 6 items
  const hasMore = payload.length > 6;

  return (
    <div className="mt-4 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {displayItems.map((entry: any, index: number) => {
          const percent = ((entry.payload.value / total) * 100).toFixed(1);
          return (
            <div key={`item-${index}`} className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }}></div>
              <span className="truncate flex-1">{capitalize(entry.value)}</span>
              <span className="text-muted-foreground font-medium">({percent}%)</span>
            </div>
          );
        })}
      </div>
      {hasMore && <p className="text-xs text-muted-foreground text-center">+{payload.length - 6} more sensor types</p>}
    </div>
  );
};

export function SensorDistributionChart({ data = [], loading = false }: SensorDistributionChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const chartData = useMemo(() => {
    return data.map((item) => ({
      name: item.type,
      value: item.count,
    }));
  }, [data]);

  const handlePieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const handlePieLeave = () => {
    setActiveIndex(undefined);
  };

  // Enhanced custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);

      return (
        <div className="bg-background/95 backdrop-blur-md border border-border/50 rounded-lg shadow-lg p-4 min-w-[160px]">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: payload[0].color }} />
            <p className="font-semibold text-sm">{capitalize(data.name)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm">
              <span className="font-medium text-primary">{data.value}</span>
              <span className="text-muted-foreground ml-1">sensors</span>
            </p>
            <p className="text-xs text-muted-foreground">{percentage}% of total deployment</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Add AI insight generation
  const aiInsight = useMemo(() => {
    if (chartData.length === 0) return null;

    const totalSensors = chartData.reduce((sum, item) => sum + item.value, 0);
    const dominantType = chartData.reduce((max, item) => (item.value > max.value ? item : max));
    const percentage = ((dominantType.value / totalSensors) * 100).toFixed(1);

    return {
      type: dominantType.name,
      percentage,
      insight: `${capitalize(dominantType.name)} sensors represent ${percentage}% of your deployment. Consider diversifying sensor types for comprehensive monitoring.`,
    };
  }, [chartData]);

  return (
    <Card className="col-span-1 md:col-span-1 lg:col-span-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span>Sensor Distribution</span>
          {!loading && chartData.length > 0 && (
            <span className="text-sm text-muted-foreground font-normal">
              {chartData.reduce((sum, item) => sum + item.value, 0)} total
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {loading ? (
          <div className="h-[280px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[280px] flex flex-col items-center justify-center text-muted-foreground">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-3">
              <Loader2 className="h-8 w-8" />
            </div>
            <p className="text-sm font-medium">No sensor data available</p>
            <p className="text-xs text-center mt-1">Sensor distribution will appear here once devices are connected</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Chart Container with responsive sizing */}
            <div className="h-[200px] sm:h-[220px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={65}
                    paddingAngle={2}
                    dataKey="value"
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={handlePieEnter}
                    onMouseLeave={handlePieLeave}
                    animationDuration={750}
                    animationBegin={0}
                    animationEasing="ease-out"
                  >
                    {chartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        stroke="rgba(255,255,255,0.2)"
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend with improved layout */}
            <div className="border-t pt-4">
              {renderLegend({
                payload: chartData.map((item, index) => ({
                  value: item.name,
                  color: COLORS[index % COLORS.length],
                  payload: { value: item.value },
                })),
              })}
            </div>

            {/* AI Insight with improved styling */}
            {aiInsight && (
              <div className="mt-4 p-3 bg-gradient-to-r from-chart-2/10 to-chart-3/10 dark:from-chart-2/20 dark:to-chart-3/20 rounded-lg border border-chart-2/20 dark:border-chart-2/40">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-chart-2/20 dark:bg-chart-2/30 flex items-center justify-center flex-shrink-0">
                    <Brain className="h-3 w-3 text-chart-2 dark:text-chart-2" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-chart-2 dark:text-chart-2">AI Insight</p>
                    <p className="text-xs text-chart-2/80 dark:text-chart-2/90 mt-1 leading-relaxed">
                      {aiInsight.insight}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
